import { ReactComponent as plusIcon } from '@epam/assets/icons/common/action-add-12.svg';
import { Button, FlexCell, FlexRow, Panel } from '@epam/promo';
import { Outlet, useNavigate } from 'react-router-dom';

import { BlankBlock } from 'components/common';
import CardFilters from 'components/common/CardFilters/CardFilters';
import { Card, CardList } from 'components/tasks';
import { GroupList } from 'components/tasks/GroupList';
import { useModal, useTaskDeleteQuery, useTaskSwitcher } from 'hooks';
import { useAppLocation } from 'hooks/helpers';
import { useRef } from 'react';
import { taskRoutes } from 'router/taskRoutes';
import { resetDraft, useTaskStore } from 'store/task';
import { useFavoritesStore } from 'store/favorites';
import css from './TaskPage.module.scss';

export const TaskPageView = () => {
  const navigate = useNavigate();
  const switchTask = useTaskSwitcher();
  const { draft: draftTask, tasks } = useTaskStore();
  const { isCreate, isNotFound } = useAppLocation(taskRoutes);
  const panelRef = useRef<HTMLDivElement>(null);
  const favoriteTaskIds = useFavoritesStore((state) => state.favoriteTaskIds);

  const { discardDraft } = useTaskDeleteQuery();
  const modal = useModal();

  const showForm = () => {
    if (isNotFound) {
      return (
        <BlankBlock
          header="Task is not found!"
          message="Please create another task"
        />
      );
    }

    if (tasks.length || isCreate) {
      return <Outlet />;
    }

    return (
      <BlankBlock
        header="Nothing to display yet"
        message="Please create your first task"
      />
    );
  };

  return (
    <FlexRow cx={css.page__container}>
      <FlexCell ref={panelRef} cx={css['page__left-panel']} grow={1}>
        <Button
          caption="Add a new task"
          color="blue"
          cx={css.page__btn}
          icon={plusIcon}
          rawProps={{ 'data-testid': 'add-task-btn' }}
          onClick={() => {
            switchTask({
              onSuccess: () => {
                navigate(taskRoutes.create);
                resetDraft();
              },
              onAbort: () => {
                navigate(taskRoutes.create);
                resetDraft();
              },
            });
          }}
        />

        {tasks.length && (
          <CardFilters
            CardList={CardList}
            favoritesFilter={{
              isFavorite: (item) => !!favoriteTaskIds[item.id],
            }}
            items={isCreate ? [draftTask, ...tasks] : tasks}
            panelHeight={panelRef.current?.clientHeight ?? 0}>
            <>
              {isCreate && (
                <Card
                  isActive
                  item={draftTask}
                  onClick={() => null}
                  onDelete={() =>
                    modal.delete({
                      text: 'Are you sure you want to Delete this draft?',
                      highlights: ['Delete', 'draft'],
                      onSuccess: () => discardDraft(),
                    })
                  }
                />
              )}
              <GroupList />
            </>
          </CardFilters>
        )}
      </FlexCell>
      <FlexCell cx={css['page__central-panel']} grow={2.5}>
        <Panel shadow cx={css.page__panel}>
          {showForm()}
        </Panel>
      </FlexCell>
      <FlexCell cx={css['page__right-panel']} grow={1} />
    </FlexRow>
  );
};
